import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarEvent } from "../types/event";
import { mergeTags } from "../utils/eventTags";
import { callPymbleApi } from "./usePymbleApi";
import { EVENT_END_POINT, EventData, toWriteShape } from "./useEventManager";

/**
 * Add tags to many events at once, from a selection made in the List view.
 *
 * ## The whole document goes back, not a tags-only patch
 *
 * For the same reason `moveEvent` sends everything: the API's update builder
 * writes `Approval.*`, `Banner`, `SelectedGroups`, `Notice` and `ActiveAt`
 * unconditionally, so a minimal body does not leave those alone — it BLANKS
 * them, and clearing `ActiveAt` would quietly withdraw a permission from
 * carers. Tagging fifty events with a small patch would therefore un-publish
 * fifty permissions, which is about the worst thing this feature could do.
 *
 * The list endpoint applies no projection, so the events the browse views hold
 * are the full documents and no extra read is needed. They are the ENRICHED
 * read shape though — `Notices` as notice documents rather than ids — so each
 * one still goes through `toWriteShape`, or any event with a notice linked
 * answers 422 `Notices.0: Input should be a valid string`.
 *
 * ## One request per event, four at a time
 *
 * There is no bulk endpoint. Fifty sequential round trips is a minute of
 * staring at a spinner and fifty at once is a burst the APIM subscription has
 * no reason to enjoy, so this walks the selection in small batches. The
 * ordering within a batch does not matter: the events are independent
 * documents and no two writes touch the same one.
 *
 * ## Partial failure is a RESULT, not an exception
 *
 * A rejected write does not stop the run and does not throw. Forty-eight
 * events tagged and two refused is the normal shape of this operation — an
 * event can be deleted between the list being fetched and the button being
 * pressed — and the caller needs to say which two, not lose the forty-eight.
 * So every outcome is collected and handed back, and the caller decides how
 * loudly to report it.
 */

export interface BulkTagOutcome {
	/** Events written successfully. */
	tagged: number;
	/** Events that already carried every chosen tag, so were never sent. */
	skipped: number;
	/** Titles of the events whose write was refused. */
	failed: string[];
}

/** Writes in flight at once. See the note above on why this is not one. */
const BATCH_SIZE = 4;

export function useBulkTag() {
	const { instance } = useMsal();
	const queryClient = useQueryClient();
	const [running, setRunning] = useState(false);

	const addTags = useCallback(
		async (
			events: CalendarEvent[],
			tags: string[],
		): Promise<BulkTagOutcome> => {
			const outcome: BulkTagOutcome = { tagged: 0, skipped: 0, failed: [] };
			if (events.length === 0 || tags.length === 0) return outcome;

			/* Worked out before anything is sent, so an event that already carries
			   the tag costs no request at all. `mergeTags` returns the SAME array
			   when nothing is new, which is what identity is being tested here. */
			/* Ids actually written, for the cache drop below — not the ids asked
			   for, which would refetch documents nothing changed. */
			const written: string[] = [];

			const writes: { event: CalendarEvent; Tags: string[] }[] = [];
			for (const event of events) {
				const existing = event.Tags ?? [];
				const next = mergeTags(existing, tags);
				if (next === existing) outcome.skipped += 1;
				else writes.push({ event, Tags: next });
			}

			setRunning(true);
			try {
				for (let i = 0; i < writes.length; i += BATCH_SIZE) {
					const batch = writes.slice(i, i + BATCH_SIZE);
					const results = await Promise.allSettled(
						batch.map(({ event, Tags }) =>
							callPymbleApi(
								instance,
								"PUT",
								EVENT_END_POINT + (event._id ?? String(event.id)),
								{
									data: toWriteShape({
										...event,
										Tags,
									} as unknown as EventData),
								},
							),
						),
					);
					results.forEach((result, n) => {
						const { event } = batch[n];
						/* A FULFILLED CALL IS NOT A WRITE. `callPymbleApi` answers
						   `null` without sending anything when MSAL has no account, or
						   when a silent token refresh fails on a tab that has been open
						   a while — the very state a person is in after spending a few
						   minutes filtering and picking events. Counting those as
						   successes is how this reported "Tagged 12 events" over twelve
						   requests that never left the browser.

						   A real PUT answers with the updated document, so a falsy
						   value means nothing was written whatever the promise says. */
						if (result.status === "fulfilled" && result.value) {
							outcome.tagged += 1;
							written.push(event._id ?? String(event.id));
							return;
						}
						/* The title and the status, never the body. An API error can
						   quote the request back, and an event document carries
						   attendee PII — so what reaches the screen is the name of the
						   event the person just chose, and what reaches the console is
						   that plus an HTTP status. */
						outcome.failed.push(event.title || "Untitled event");
						if (result.status === "rejected") {
							const status = (
								result.reason as { response?: { status?: number } } | undefined
							)?.response?.status;
							console.error(`Could not tag event: ${status ?? "no response"}`);
						} else {
							console.error("Could not tag event: no answer from the API");
						}
					});
				}
			} finally {
				setRunning(false);
			}

			if (outcome.tagged > 0) {
				/* The same invalidation the single-event save does, and for the same
				   reasons — `staleTime` is an hour and nothing refetches on mount, so
				   without this the rows keep their old chips and the rail keeps its
				   old counts until it expires.

				   `["events"]` bare, covering all three caches (the calendar's
				   ranges, the browse months, the search results). `["tags"]` because
				   a tag nobody had used is added to the shared vocabulary as the
				   first event carrying it saves.

				   AND `["event", "v2", id]` FOR EVERY EVENT WRITTEN, which this
				   missed. That cache is what the editor reads, so opening a
				   just-tagged event served the document as it stood before the write
				   — no tag on the Details tab, for the hour until the entry went
				   stale. Opening one to check is exactly how somebody verifies a bulk
				   write, so the one path that reported the feature broken was the
				   first path anybody took. The publish keys go too, for the same
				   reason the single-event save drops them: the server decides things
				   the request cannot, and they only reach the screen on a refetch. */
				queryClient.invalidateQueries({ queryKey: ["events"] });
				queryClient.invalidateQueries({ queryKey: ["tags"] });
				queryClient.invalidateQueries({ queryKey: ["publish", "list"] });
				for (const id of written) {
					queryClient.invalidateQueries({ queryKey: ["event", "v2", id] });
					queryClient.invalidateQueries({
						queryKey: ["publish", "workflow", id],
					});
				}
			}

			return outcome;
		},
		[instance, queryClient],
	);

	return { addTags, running };
}
